import React, { memo, useMemo, useState } from 'react';
import {
	matchedScopes,
	ScreenSpaceFlagsPlacementComponent,
} from '@post-office-placement/screen-space-flags/placement';

import Placement from '@atlassian/post-office-placement/placement';
import { ScreenSpaceFlagPlacementPreviewSwitcher } from '@atlassian/post-office-placement/preview';
import {
	PlacementFetchProvider,
	usePlacementPreloadedData,
} from '@atlassian/confluence-post-office-provider/entry-points/preload-placement';

import { ErrorDisplay } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { fg } from '@confluence/feature-gating';
import type { PreloadFnContext } from '@confluence/query-preloader-tools';
import type { RouteMatch } from '@confluence/route';
import { useSessionData } from '@confluence/session-data';

import { preloadPostOfficePlacementWithSessionContext } from './preload-placement';
import { sessionToPostOfficeContext } from './session-to-post-office-context';

export const ScreenSpaceFlagsPlacement = memo(() => {
	const [messageErrorToReport, setMessageErrorToReport] = useState<any | undefined>();

	const [preloadedInitialData, setPreloadedInitialData] = usePlacementPreloadedData(
		'screen-space-flags/update-messages',
	);

	const memoizedPlacementProps = useMemo(
		() => ({
			reportExpectedError: markErrorAsHandled,
			reportUnexpectedError: setMessageErrorToReport,
			preloadedInitialData,
			setPreloadedInitialData,
		}),
		[preloadedInitialData, setPreloadedInitialData],
	);

	const { userId } = useSessionData();
	if (fg('confluence-screen-space-overlay-no-anonymous-users')) {
		if (!userId) {
			return null;
		}
	}

	return fg('post-office-screen-space-overlay-confluence') ? (
		<ScreenSpaceFlagPlacementPreviewSwitcher isEnabled>
			<PlacementFetchProvider>
				<Placement
					placementId="screen-space-flags"
					// @ts-ignore - Type 'ComponentType<ErrorBoundariedProps<Placement>>' is not assignable to type 'PlacementComponent<LocalPlacementProps>'
					PlacementComponent={ScreenSpaceFlagsPlacementComponent}
					placementProps={memoizedPlacementProps}
					disableInitialData
				/>

				{messageErrorToReport && <ErrorDisplay error={messageErrorToReport} />}
			</PlacementFetchProvider>
		</ScreenSpaceFlagPlacementPreviewSwitcher>
	) : null;
});

export const preloadPostOfficeScreenSpaceFlags = async (
	preloadedSessionContext: PreloadFnContext,
	match: RouteMatch | null,
) => {
	const activeScopes = matchedScopes({
		route: {
			hostname: '',
			pathname: match?.pathname ?? '',
			search: match?.search ?? '',
		},
		context: sessionToPostOfficeContext(preloadedSessionContext),
	});

	const updateMessagesBody = {
		current: activeScopes,
		previous: [],
	};

	return preloadPostOfficePlacementWithSessionContext(
		'screen-space-flags',
		preloadedSessionContext,
		{
			resource: '/update-messages',
			requestInit: {
				method: 'POST',
				body: JSON.stringify(updateMessagesBody),
			},
		},
	);
};
